import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  Contract,
  ContractTemplate,
  ContractGenerationRequest,
  ContractGenerationResponse,
  ContractDocumentsResponse,
  B2bContract,
  B2bContractPagedResponse,
  B2bContractDetail,
  B2bContractDriver,
  B2bClaim,
  B2bOperationalCharge,
  B2bVehicleReservation,
  B2bAccessory,
  B2bContractItemAccessory,
  B2bBillingPeriod,
  B2bTrafficFine,
  B2bVehicleSubstitution,
  B2bVehicleReturnEvent,
  B2bFleetDelivery,
  B2bContractServiceOrder,
  ContractFreezePayload,
  ContractFreezeResponse,
  ContractUnfreezeResponse,
  ContractActionsResponse,
  ContractActionLog
} from '../models/Contract';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class ContractsService {
  // Respeita o environment (local → localhost, dev → erp-dev-contract, prod → conforme environment.prod)
  private baseUrl = environment.microservices?.contract ?? environment.apiUrl ?? '';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
    private authService: AuthService
  ) {}

  /** JSON + JWT Bearer para chamadas ao microserviço de contratos. */
  private jsonAuthHeaders(): HttpHeaders {
    let h = new HttpHeaders({ 'Content-Type': 'application/json' });
    const raw = this.authService.getToken();
    const token = typeof raw === 'string' ? raw.trim() : '';
    if (token) {
      h = h.set('Authorization', `Bearer ${token}`);
    }
    return h;
  }

  /** Apenas Bearer (ex.: `FormData` sem definir `Content-Type`). */
  private bearerHeaders(): HttpHeaders {
    const raw = this.authService.getToken();
    const token = typeof raw === 'string' ? raw.trim() : '';
    let h = new HttpHeaders();
    if (token) {
      h = h.set('Authorization', `Bearer ${token}`);
    }
    return h;
  }

  /**
   * Adiciona o código de autenticação da Azure Function à URL quando necessário
   * @param url URL original
   * @returns URL com código de autenticação se necessário
   */
  private addFunctionCode(url: string): string {
    // Em desenvolvimento local, não adiciona o code
    if (!environment.production) {
      return url;
    }

    // Se a URL já tem o parâmetro code, não adiciona novamente
    if (url.includes('code=')) {
      return url;
    }

    // Retorna URL original (código de autenticação não está configurado neste serviço)
    return url;
  }

  // CRUD de Contratos

  /** Resposta paginada da listagem de contratos. filter = busca livre (placa, nome motorista, número contrato, CPF, etc). */
  listContractsPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters?: { filter?: string; status?: string }
  ): Observable<{ contracts: Contract[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    if (filters?.filter?.trim()) {
      params = params.set('filter', filters.filter.trim());
    }
    if (filters?.status?.trim()) {
      params = params.set('status', filters.status.trim());
    }
    const url = `${this.baseUrl}/list/contracts`;
    return this.http.get<any>(url, { params, headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        // API retorna os contratos em "items" (não "contracts")
        const list = response?.items ?? response?.contracts ?? response?.data ?? (Array.isArray(response) ? response : []);
        const contracts = Array.isArray(list) ? list.map((dto: any) => this.mapContractFromDto(dto)) : [];
        const totalCount = response?.totalCount ?? response?.total ?? contracts.length;
        const currentPage = response?.page ?? page;
        const size = response?.pageSize ?? pageSize;
        const totalPages = response?.totalPages ?? Math.max(1, Math.ceil(totalCount / size));
        return { contracts, totalCount, page: currentPage, pageSize: size, totalPages };
      }),
      catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); })
    );
  }

  /**
   * Dashboard de contratos: totais e contagem por status.
   * GET {{baseUrl}}/list/contracts/dashboard
   */
  getContractsDashboard(): Observable<{ totalCount: number; countsByStatus: { status: string; count: number }[] }> {
    const url = `${this.baseUrl}/list/contracts/dashboard`;
    return this.http.get<any>(url, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        const totalCount = response?.TotalCount ?? response?.totalCount ?? 0;
        const raw = response?.CountsByStatus ?? response?.countsByStatus ?? [];
        const countsByStatus = Array.isArray(raw)
          ? raw.map((x: any) => ({ status: x.Status ?? x.status ?? '', count: x.Count ?? x.count ?? 0 }))
          : [];
        return { totalCount, countsByStatus };
      }),
      catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); })
    );
  }

  listContracts(): Observable<Contract[]> {
    return this.http
      .get<any>(`${this.baseUrl}/list/contracts`, { headers: this.jsonAuthHeaders() })
      .pipe(
      map(response => {
        // Verificar se a resposta tem a estrutura esperada
        if (response.success && response.contracts) {
          return response.contracts.map((dto: any) => this.mapContractFromDto(dto));
        } else if (Array.isArray(response)) {
          // Fallback para resposta direta em array
          return response.map((dto: any) => this.mapContractFromDto(dto));
        } else {
          return [];
        }
      }),
      catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); })
    );
  }

  searchContracts(params: {
    q?: string;
    status?: string;
    productId?: number;
    customerId?: number;
    vehicleId?: number;
    startDate?: string;
    endDate?: string;
  }): Observable<Contract[]> {
    let queryParams = new URLSearchParams();
    
    if (params.q) queryParams.append('q', params.q);
    if (params.status) queryParams.append('status', params.status);
    if (params.productId) queryParams.append('productId', params.productId.toString());
    if (params.customerId) queryParams.append('customerId', params.customerId.toString());
    if (params.vehicleId) queryParams.append('vehicleId', params.vehicleId.toString());
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

  const queryString = queryParams.toString();
  const url = `${this.baseUrl}/search/contracts${queryString ? '?' + queryString : ''}`;


    return this.http.get<any>(url, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        
        // Verificar se a resposta tem a estrutura esperada
        if (response.success && response.contracts) {
          return response.contracts.map((dto: any) => this.mapContractFromDto(dto));
        } else if (Array.isArray(response)) {
          // Fallback para resposta direta em array
          return response.map((dto: any) => this.mapContractFromDto(dto));
        } else {
          return [];
        }
      }),
      catchError(err => { 
        console.error('❌ Erro na busca:', err);
        this.errorService.handleHttpError(err); 
        return throwError(() => err); 
      })
    );
  }

  /**
   * Busca contratos de um cliente por customerId.
   * GET /list/contracts/customer/{customerId}
   */
  getContractsByCustomerId(customerId: number): Observable<Contract[]> {
    const url = `${this.baseUrl}/list/contracts/customer/${customerId}`;
    return this.http.get<any>(url, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        const list = response?.items ?? response?.contracts ?? response?.data ?? (Array.isArray(response) ? response : []);
        return Array.isArray(list) ? list.map((dto: any) => this.mapContractFromDto(dto)) : [];
      }),
      catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); })
    );
  }

  getContract(id: number): Observable<Contract> {
    return this.http
      .get<any>(`${this.baseUrl}/view/contracts/${id}`, { headers: this.jsonAuthHeaders() })
      .pipe(
      map(dto => this.mapContractFromDto(dto)),
      catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); })
    );
  }

  getContractComplete(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/view/contracts/${id}/complete`, { headers: this.jsonAuthHeaders() })
      .pipe(
      map(response => {
        return {
          contract: this.mapContractFromDto(response.contract),
          customer: this.mapCustomerFromDto(response.customer),
          vehicle: this.mapVehicleFromDto(response.vehicle),
          product: response.product,
          driver: this.mapDriverFromDto(response.driver),
          vehicleSnapshot: response.vehicleSnapshot,
          documents: response.documents || [],
          summary: response.summary || {}
        };
      }),
      catchError(err => { 
        console.error('❌ Erro ao buscar contrato completo:', err);
        this.errorService.handleHttpError(err); 
        return throwError(() => err); 
      })
    );
  }

  /**
   * Histórico de ações / auditoria do contrato (congelar, assinar, renovar, frota, etc.).
   * GET {{baseUrl}}/view/contratos/{id}/acoes
   */
  getContractActions(contractId: number): Observable<ContractActionsResponse> {
    const url = `${this.baseUrl}/view/contratos/${contractId}/acoes`;
    return this.http
      .get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() })
      .pipe(map(raw => this.mapContractActionsResponse(raw, contractId)));
  }

  private mapContractActionsResponse(raw: any, contractId: number): ContractActionsResponse {
    const listRaw = raw?.acoes ?? raw?.Acoes ?? [];
    const acoes: ContractActionLog[] = Array.isArray(listRaw)
      ? listRaw.map((a: any) => this.mapContractActionLogDto(a))
      : [];
    return {
      idContrato: Number(raw?.idContrato ?? raw?.IdContrato ?? contractId) || contractId,
      quantidadeRegistros: Number(raw?.quantidadeRegistros ?? raw?.QuantidadeRegistros ?? acoes.length) || acoes.length,
      acoes
    };
  }

  private mapContractActionLogDto(a: any): ContractActionLog {
    return {
      idRascunhoContrato: a?.idRascunhoContrato ?? a?.IdRascunhoContrato ?? null,
      tipoAcao: a?.tipoAcao ?? a?.TipoAcao ?? null,
      tipoAcaoDescricao: a?.tipoAcaoDescricao ?? a?.TipoAcaoDescricao ?? null,
      categoriaAcao: a?.categoriaAcao ?? a?.CategoriaAcao ?? null,
      ocorridoEm: a?.ocorridoEm ?? a?.OcorridoEm ?? null,
      idColaboradorExecutor: a?.idColaboradorExecutor ?? a?.IdColaboradorExecutor ?? null,
      nomeColaboradorExecutor: a?.nomeColaboradorExecutor ?? a?.NomeColaboradorExecutor ?? null,
      idUsuarioExecutor: a?.idUsuarioExecutor ?? a?.IdUsuarioExecutor ?? null,
      emailUsuarioExecutor: a?.emailUsuarioExecutor ?? a?.EmailUsuarioExecutor ?? null,
      nomeExibicaoExecutor: a?.nomeExibicaoExecutor ?? a?.NomeExibicaoExecutor ?? null,
      resumo: a?.resumo ?? a?.Resumo ?? null,
      detalhesJson: a?.detalhesJson ?? a?.DetalhesJson ?? null,
      tipoEntidadeRelacionada: a?.tipoEntidadeRelacionada ?? a?.TipoEntidadeRelacionada ?? null,
      idEntidadeRelacionada: a?.idEntidadeRelacionada ?? a?.IdEntidadeRelacionada ?? null,
      statusAnterior: a?.statusAnterior ?? a?.StatusAnterior ?? null,
      novoStatus: a?.novoStatus ?? a?.NovoStatus ?? null,
      enderecoIp: a?.enderecoIp ?? a?.EnderecoIp ?? null,
      agenteUsuario: a?.agenteUsuario ?? a?.AgenteUsuario ?? null,
      geradoPorSistema: a?.geradoPorSistema ?? a?.GeradoPorSistema ?? null,
      idCorrelacao: a?.idCorrelacao ?? a?.IdCorrelacao ?? null,
      id: a?.id ?? a?.Id ?? null,
      criadoEm: a?.criadoEm ?? a?.CriadoEm ?? null,
      criadoPor: a?.criadoPor ?? a?.CriadoPor ?? null,
      excluido: a?.excluido ?? a?.Excluido ?? null
    };
  }

  // Buscar documentos versionados do contrato
  getContractDocuments(contractId: number): Observable<ContractDocumentsResponse> {
    return this.http
      .get<any>(`${this.baseUrl}/contracts/${contractId}/documents`, { headers: this.jsonAuthHeaders() })
      .pipe(
      map(response => {
        
        // Validar estrutura da resposta
        if (response.success && response.contrato && response.documentos) {
          return {
            success: response.success,
            contrato: response.contrato,
            documentos: response.documentos.map((doc: any) => ({
              id: doc.id,
              versao: doc.versao,
              versaoFormatada: doc.versaoFormatada || `v${doc.versao}`,
              documentType: doc.documentType || 'Contract',
              storageKey: doc.storageKey,
              storageUrl: doc.storageUrl,
              signatureProvider: doc.signatureProvider || 'Pendente',
              providerDocumentId: doc.providerDocumentId || null,
              signatureStatus: doc.signatureStatus || 'Pending',
              isPendente: doc.isPendente !== undefined ? doc.isPendente : (doc.signatureStatus === 'Pending' || doc.signatureStatus === 'Pendente'),
              isAssinado: doc.isAssinado !== undefined ? doc.isAssinado : (doc.signatureStatus === 'SIGNED' || doc.signatureStatus === 'Signed'),
              textSummary: doc.textSummary || '',
              fileSizeBytes: doc.fileSizeBytes || 0,
              contentType: doc.contentType || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              createdAt: doc.createdAt,
              updatedAt: doc.updatedAt || null,
              podeAssinar: doc.podeAssinar !== undefined ? doc.podeAssinar : doc.isPendente
            })),
            resumo: response.resumo || {
              totalVersoes: response.documentos?.length || 0,
              versoesAssinadas: response.documentos?.filter((d: any) => d.isAssinado || d.signatureStatus === 'SIGNED' || d.signatureStatus === 'Signed').map((d: any) => d.versaoFormatada || `v${d.versao}`) || [],
              versoesPendentes: response.documentos?.filter((d: any) => d.isPendente || d.signatureStatus === 'Pending' || d.signatureStatus === 'Pendente').map((d: any) => d.versaoFormatada || `v${d.versao}`) || [],
              proximaVersao: (response.documentos?.length || 0) + 1
            }
          };
        }
        
        // Fallback para estrutura direta
        return response as ContractDocumentsResponse;
      }),
      catchError(err => { 
        console.error('❌ Erro ao buscar documentos do contrato:', err);
        this.errorService.handleHttpError(err); 
        return throwError(() => err); 
      })
    );
  }

  // Criar contrato com nova estrutura de payload
  createContract(contractData: {
    contract: {
      productId: number;
      customerId: number;
      vehicleId: number;
      documentTemplateVersionId: number;
      startDate: string;
      endDate: string;
      billingCycle: string;
      depositPaid: boolean;
      depositPaidDate: string | null;
      firstInstallmentPaid: boolean;
      firstInstallmentPaidDate: string | null;
      placeholdersDataJson: string;
      status?: string;
    };
    driver: {
      name: string;
      cpfCnpj: string;
      cnhNumber: string;
      cnhExpiryDate: string;
      email: string;
      phone: string;
      street: string;
      number: string;
      complement: string;
      district: string;
      city: string;
      state: string;
      zipCode: string;
    };
    vehicleSnapshot: {
      plate: string;
      model: string;
      year: number;
      color: string;
      brand: string;
      renavam: string;
      chassis: string;
      currentKm: number;
      inspectionNotes: string;
    };
  }): Observable<any> {
    // Sanitizar e garantir a estrutura exata do payload.
    // Caução e 1ª parcela só vão como true se o chamador passar explicitamente true (evita backend gravar “pago” por engano).
    const depositPaid = contractData.contract.depositPaid === true;
    const firstInstallmentPaid = contractData.contract.firstInstallmentPaid === true;
    const sanitized = {
      contract: {
        productId: Number(contractData.contract.productId),
        customerId: Number(contractData.contract.customerId),
        vehicleId: Number(contractData.contract.vehicleId),
        documentTemplateVersionId: Number(contractData.contract.documentTemplateVersionId),
        startDate: contractData.contract.startDate,
        endDate: contractData.contract.endDate,
        billingCycle: contractData.contract.billingCycle,
        depositPaid,
        depositPaidDate: depositPaid ? (contractData.contract.depositPaidDate ?? null) : null,
        firstInstallmentPaid,
        firstInstallmentPaidDate: firstInstallmentPaid ? (contractData.contract.firstInstallmentPaidDate ?? null) : null,
        placeholdersDataJson: contractData.contract.placeholdersDataJson,
        status: contractData.contract.status ?? 'PED. Assinatura'
      },
      driver: {
        name: contractData.driver.name,
        cpfCnpj: contractData.driver.cpfCnpj,
        cnhNumber: contractData.driver.cnhNumber,
        cnhExpiryDate: contractData.driver.cnhExpiryDate,
        email: contractData.driver.email,
        phone: contractData.driver.phone,
        street: contractData.driver.street,
        number: contractData.driver.number,
        complement: contractData.driver.complement,
        district: contractData.driver.district,
        city: contractData.driver.city,
        state: contractData.driver.state,
        zipCode: contractData.driver.zipCode
      },
      vehicleSnapshot: {
        plate: contractData.vehicleSnapshot.plate,
        model: contractData.vehicleSnapshot.model,
        year: Number(contractData.vehicleSnapshot.year),
        color: contractData.vehicleSnapshot.color,
        brand: contractData.vehicleSnapshot.brand,
        renavam: contractData.vehicleSnapshot.renavam,
        chassis: contractData.vehicleSnapshot.chassis,
        currentKm: Number(contractData.vehicleSnapshot.currentKm),
        inspectionNotes: contractData.vehicleSnapshot.inspectionNotes
      }
    };

    return this.http.post<any>(`${this.baseUrl}/create/contracts`, sanitized, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        
        if (response.success) {
          return {
            success: true,
            message: response.message,
            contract: this.mapContractFromDto(response.contract),
            document: response.document
          };
        }
        
        return response;
      }),
      catchError(err => { 
        console.error('❌ Erro ao criar contrato:', err);
        this.errorService.handleHttpError(err); 
        return throwError(() => err); 
      })
    );
  }

  updateContract(id: number, contractData: Omit<Contract, 'createdAt' | 'updatedAt'>): Observable<Contract> {
    return this.http.put<any>(`${this.baseUrl}/edit/contracts/${id}`, contractData, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        
        // Verificar se a resposta tem a estrutura esperada
        if (response.success && response.contract) {
          return this.mapContractFromDto(response.contract);
        } else if (response.id) {
          // Fallback para resposta direta do contrato
          return this.mapContractFromDto(response);
        } else {
          return this.mapContractFromDto(response);
        }
      }),
      catchError(err => {
        console.error('❌ Erro ao atualizar contrato:', err);
        this.errorService.handleHttpError(err); 
        return throwError(() => err); 
      })
    );
  }

  deleteContract(id: number): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/delete/contracts/${id}`, { headers: this.jsonAuthHeaders() })
      .pipe(
      catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); })
    );
  }

  // Assinar contrato
  signContract(contractId: number, signatureData: {
    signature: string;
  }): Observable<any> {
    
    return this.http.post<any>(`${this.baseUrl}/contracts/${contractId}/assinar`, signatureData, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        
        // Verificar estrutura da resposta
        if (response.success) {
          return {
            success: true,
            message: response.message || 'Contrato assinado com sucesso',
            signedDocumentUrl: response.signedDocumentUrl || response.documentUrl || null,
            contract: response.contract || null
          };
        }
        
        return response;
      }),
      catchError(err => { 
        console.error('❌ Erro ao assinar contrato:', err);
        this.errorService.handleHttpError(err); 
        return throwError(() => err); 
      })
    );
  }

  // Upload de contrato assinado
  uploadSignedContract(contractId: number, signatureData: {
    signatureBase64: string;
    signedDocumentBase64?: string;
    signedAt: string;
    signedBy: string;
  }): Observable<any> {
    
    return this.http.post<any>(`${this.baseUrl}/contracts/${contractId}/salvar-assinado`, signatureData, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        
        // Verificar estrutura da resposta
        if (response.success) {
          return {
            success: true,
            message: response.message || 'Contrato assinado salvo com sucesso',
            signedDocumentUrl: response.signedDocumentUrl || response.documentUrl || null,
            contract: response.contract || null
          };
        }
        
        return response;
      }),
      catchError(err => { 
        console.error('❌ Erro ao fazer upload do contrato assinado:', err);
        this.errorService.handleHttpError(err); 
        return throwError(() => err); 
      })
    );
  }

  // Geração de Contratos
  generateContract(request: ContractGenerationRequest): Observable<ContractGenerationResponse> {
    return this.http.post<any>(`${this.baseUrl}/contracts/generate`, request, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => ({
        contract: this.mapContractFromDto(response.contract),
        generatedDocumentUrl: response.generatedDocumentUrl,
        placeholdersReplaced: response.placeholdersReplaced || {}
      })),
      catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); })
    );
  }

  // Templates de Contrato - Seleção para criação
  getAvailableTemplates(): Observable<any[]> {
    return this.http
      .get<any>(`${this.baseUrl}/select/templates`, { headers: this.jsonAuthHeaders() })
      .pipe(
      map(response => {
        
        if (response.success && response.templates) {
          return response.templates.map((template: any) => ({
            id: template.id,
            name: template.name,
            area: template.area,
            observation: template.observation,
            versions: template.versions || []
          }));
        }
        
        return response.templates || [];
      }),
      catchError(err => { 
        console.error('❌ Erro ao carregar templates para seleção:', err);
        this.errorService.handleHttpError(err); 
        return throwError(() => err); 
      })
    );
  }

  // Obter detalhes de uma versão específica
  getTemplateVersionDetails(templateId: number, versionId: number): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/select/templates/${templateId}/versions/${versionId}`, {
        headers: this.jsonAuthHeaders()
      })
      .pipe(
      map(response => {
        return response;
      }),
      catchError(err => { 
        console.error('❌ Erro ao carregar detalhes da versão:', err);
        this.errorService.handleHttpError(err); 
        return throwError(() => err); 
      })
    );
  }

  getTemplateVersions(templateId: number): Observable<any[]> {
    return this.http
      .get<any[]>(`${this.baseUrl}/list/contract-templates/${templateId}/versions`, {
        headers: this.jsonAuthHeaders()
      })
      .pipe(
      map(list => {
        // Mapear estrutura: VersionNumber, Status, Id
        return (list || []).map(v => ({
          id: v.Id ?? v.id,
          version: v.VersionNumber ?? v.version ?? 0,
          status: v.Status ?? v.status ?? '',
          createdAt: v.CreatedAt ?? v.createdAt ?? null,
          storageUrl: v.StorageUrl ?? v.storageUrl ?? null
        }));
      }),
      catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); })
    );
  }

  getLatestTemplateVersion(templateId: number): Observable<ContractTemplate> {
    return this.http
      .get<any>(`${this.baseUrl}/list/contract-templates/${templateId}/versions`, {
        headers: this.jsonAuthHeaders()
      })
      .pipe(
      map(response => {
        // Se a resposta é um array, pegar o primeiro (mais recente)
        if (Array.isArray(response)) {
          return this.mapTemplateFromDto(response[0]);
        }
        // Se é um objeto único, usar diretamente
        return this.mapTemplateFromDto(response);
      }),
      catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); })
    );
  }

  // Download de documentos
  downloadContractDocument(contractId: number): Observable<{ blob: Blob; fileName: string; mimeType: string }> {
    const url = `${this.baseUrl}/contracts/${contractId}/download`;
    
    let headers = new HttpHeaders({
      Accept:
        'application/octet-stream, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, */*'
    });
    const raw = this.authService.getToken();
    const token = typeof raw === 'string' ? raw.trim() : '';
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob',
      headers
    }).pipe(
      map((res: any) => {
        const contentDisposition = res.headers.get('Content-Disposition') || '';
        const fileName = this.extractFileNameFromContentDisposition(contentDisposition) || 'contract.pdf';
        const mimeType = res.headers.get('Content-Type') || 'application/pdf';
        return { blob: res.body as Blob, fileName, mimeType };
      }),
      catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); })
    );
  }

  // Substituição de placeholders
  previewContractWithData(templateId: number, placeholderData: { [key: string]: any }): Observable<string> {
    return this.http.post<{ content: string }>(`${this.baseUrl}/contracts/templates/${templateId}/preview`, 
      { placeholders: placeholderData }, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => response.content),
      catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); })
    );
  }

  // Mapeamento de DTOs
  private mapContractFromDto(dto: any): Contract {
    return {
      id: dto.id || 0,
      contractNumber: `${dto.id?.toString().padStart(4, '0') || '0000'}`,
      customerId: dto.customerId || 0,
      vehicleId: dto.vehicleId || 0,
      productId: dto.productId || 0,
      documentTemplateVersionId: dto.documentTemplateVersionId || 0,
      
      startDate: dto.startDate || '',
      endDate: dto.endDate || '',
      
      billingCycle: dto.billingCycle || '',
      baseAmount: dto.baseAmount || 0,
      depositAmount: dto.depositAmount || 0,
      // Só considera pago quando a API enviar explicitamente true (evita 1, "true", PascalCase etc.)
      depositPaid: dto.depositPaid === true || dto.DepositPaid === true,
      depositPaidDate: dto.depositPaidDate ?? dto.DepositPaidDate ?? undefined,
      firstInstallmentPaid: dto.firstInstallmentPaid === true || dto.FirstInstallmentPaid === true,
      firstInstallmentPaidDate: dto.firstInstallmentPaidDate ?? dto.FirstInstallmentPaidDate ?? undefined,
      
      status: dto.status || 'Draft',
      placeholdersDataJson: dto.placeholdersDataJson || '',
      
      driver: dto.driver ? {
        name: dto.driver.name || '',
        cpfCnpj: dto.driver.cpfCnpj || '',
        cnhNumber: dto.driver.cnhNumber || '',
        cnhExpiryDate: dto.driver.cnhExpiryDate || '',
        email: dto.driver.email || '',
        phone: dto.driver.phone || '',
        street: dto.driver.street || '',
        number: dto.driver.number || '',
        complement: dto.driver.complement || '',
        district: dto.driver.district || '',
        city: dto.driver.city || '',
        state: dto.driver.state || '',
        zipCode: dto.driver.zipCode || ''
      } : {
        name: '',
        cpfCnpj: '',
        cnhNumber: '',
        cnhExpiryDate: '',
        email: '',
        phone: '',
        street: '',
        number: '',
        complement: '',
        district: '',
        city: '',
        state: '',
        zipCode: ''
      },
      
      vehicleSnapshot: dto.vehicleSnapshot ? {
        plate: dto.vehicleSnapshot.plate || '',
        model: dto.vehicleSnapshot.model || '',
        year: dto.vehicleSnapshot.year || 0,
        color: dto.vehicleSnapshot.color || '',
        brand: dto.vehicleSnapshot.brand || '',
        renavam: dto.vehicleSnapshot.renavam || '',
        chassis: dto.vehicleSnapshot.chassis || '',
        currentKm: dto.vehicleSnapshot.currentKm || 0,
        inspectionNotes: dto.vehicleSnapshot.inspectionNotes || ''
      } : {
        plate: '',
        model: '',
        year: 0,
        color: '',
        brand: '',
        renavam: '',
        chassis: '',
        currentKm: 0,
        inspectionNotes: ''
      },
      
      // Checklist de saída do veículo
      checklistPerformed: dto.checklistPerformed || false,
      checklistId: dto.checklistId || undefined,
      checklistPerformedDate: dto.checklistPerformedDate || undefined,
      
      createdAt: dto.createdAt || '',
      updatedAt: dto.updatedAt || '',
      createdBy: dto.createdBy || '',
      updatedBy: dto.updatedBy || ''
    };
  }

  private mapTemplateFromDto(dto: any): ContractTemplate {
    return {
      id: dto.id || dto.Id || 0,
      name: dto.name || dto.Name || '',
      area: dto.area || dto.Area || '',
      description: dto.description || dto.Description || '',
      version: dto.version || dto.Version || 0,
      templateContent: dto.templateContent || dto.TemplateContent || '',
      placeholders: dto.placeholders || dto.Placeholders || [],
      isActive: dto.isActive !== undefined ? dto.isActive : dto.IsActive
    };
  }

  private mapCustomerFromDto(dto: any): any {
    if (!dto) return null;
    
    return {
      id: dto.id || 0,
      companyName: dto.name || '',  // API retorna "name"
      documentNumber: dto.document || '',  // API retorna "document"
      email: dto.email || '',
      phone: dto.phone || '',
      address: dto.address || {}
    };
  }

  private mapVehicleFromDto(dto: any): any {
    if (!dto) return null;
    
    return {
      id: dto.id || 0,
      plateNumber: dto.plate || '',  // API retorna "plate"
      model: dto.model || '',
      modelYear: dto.year || 0,  // API retorna "year"
      color: dto.color || '',
      brand: dto.brand || '',
      chassisNumber: dto.chassis || '',  // API retorna "chassis"
      currentMileage: dto.currentKm || 0,  // API retorna "currentKm"
      status: dto.status || '',
      category: dto.category || '',
      fuelType: dto.fuelType || '',
      engineCapacity: dto.engineCapacity || '',
      renavam: dto.renavam || ''  // API retorna "renavam"
    };
  }

  private mapDriverFromDto(dto: any): any {
    if (!dto) return null;
    
    return {
      name: dto.name || '',
      cpfCnpj: dto.cpfCnpj || '',
      cnhNumber: dto.cnhNumber || '',
      cnhExpiryDate: dto.cnhExpiryDate || '',
      email: dto.email || '',
      phone: dto.phone || '',
      street: dto.address?.street || '',
      number: dto.address?.number || '',
      complement: dto.address?.complement || '',
      district: dto.address?.district || '',
      city: dto.address?.city || '',
      state: dto.address?.state || '',
      zipCode: dto.address?.zipCode || ''
    };
  }

  private extractFileNameFromContentDisposition(cd: string): string | null {
    if (!cd) return null;
    const fileNameMatch = cd.match(/filename\*?=([^;]+)/i);
    if (fileNameMatch && fileNameMatch[1]) {
      const raw = fileNameMatch[1].trim().replace(/^UTF-8''/i, '').replace(/"/g, '');
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
    return null;
  }

  // Renovar contrato (PUT) - Carrega dados completos do contrato antes
  renewContract(contractId: number, renewalData?: {
    migracaoDePlano?: boolean;
    contract: any;
    driver: any;
    vehicleSnapshot: any;
  }): Observable<any> {
    // Se não forneceu dados, buscar do contrato completo
    if (!renewalData) {
      return this.getContractComplete(contractId).pipe(
        map(contractData => {
          // Preparar dados para renovação baseados no contrato atual
          const renewalPayload = {
            migracaoDePlano: false,
            contract: {
              customerId: contractData.contract.customerId || 1,
              vehicleId: contractData.contract.vehicleId || 1,
              productId: contractData.contract.productId,
              documentTemplateVersionId: contractData.contract.documentTemplateVersionId || 2,
              startDate: new Date().toISOString().split('T')[0] + 'T00:00:00Z',
              endDate: contractData.contract.endDate, // Pode ser ajustado
              billingCycle: contractData.contract.billingCycle || 'Mensal',
              status: 'Pendente',
              placeholdersDataJson: contractData.contract.placeholdersDataJson || '{}',
              signatureProvider: 'ClickSign'
            },
            driver: contractData.driver ? {
              name: contractData.driver.name,
              cpfCnpj: contractData.driver.cpfCnpj,
              cnhNumber: contractData.driver.cnhNumber,
              cnhExpiryDate: contractData.driver.cnhExpiryDate,
              email: contractData.driver.email,
              phone: contractData.driver.phone,
              street: contractData.driver.street,
              number: contractData.driver.number,
              complement: contractData.driver.complement || '',
              district: contractData.driver.district,
              city: contractData.driver.city,
              state: contractData.driver.state,
              zipCode: contractData.driver.zipCode
            } : null,
            vehicleSnapshot: contractData.vehicleSnapshot ? {
              plate: contractData.vehicleSnapshot.plate,
              model: contractData.vehicleSnapshot.model,
              year: contractData.vehicleSnapshot.year,
              color: contractData.vehicleSnapshot.color,
              brand: contractData.vehicleSnapshot.brand,
              renavam: contractData.vehicleSnapshot.renavam,
              chassis: contractData.vehicleSnapshot.chassis,
              currentKm: contractData.vehicleSnapshot.currentKm,
              inspectionNotes: contractData.vehicleSnapshot.inspectionNotes
            } : null
          };

          // Fazer a renovação com PUT
          return this.http.put<any>(`${this.baseUrl}/contracts/${contractId}/renovar`, renewalPayload, { headers: this.jsonAuthHeaders() }).pipe(
            map(response => {
              
              if (response.success) {
                return {
                  success: true,
                  message: response.message || 'Contrato renovado com sucesso!',
                  contract: response.contract ? this.mapContractFromDto(response.contract) : null
                };
              }
              
              return response;
            }),
            catchError(err => { 
              console.error('❌ Erro ao renovar contrato:', err);
              this.errorService.handleHttpError(err); 
              return throwError(() => err); 
            })
          );
        }),
        catchError(err => {
          console.error('❌ Erro ao carregar contrato para renovação:', err);
          this.errorService.handleHttpError(err);
          return throwError(() => err);
        })
      ).pipe(
        // Flatten do Observable interno
        switchMap(observable => observable)
      );
    }

    // Se forneceu dados, usar diretamente
    return this.http.put<any>(`${this.baseUrl}/contracts/${contractId}/renovar`, renewalData, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        
        if (response.success) {
          return {
            success: true,
            message: response.message || 'Contrato renovado com sucesso!',
            contract: response.contract ? this.mapContractFromDto(response.contract) : null
          };
        }
        
        return response;
      }),
      catchError(err => { 
        console.error('❌ Erro ao renovar contrato:', err);
        this.errorService.handleHttpError(err); 
        return throwError(() => err); 
      })
    );
  }

  // Confirmar pagamento de caução
  confirmDepositPayment(contractId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/contracts/${contractId}/confirm-deposit`, {}, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        return response;
      }),
      catchError(err => {
        console.error('❌ Erro ao confirmar pagamento de caução:', err);
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  // Confirmar pagamento da 1ª parcela
  confirmFirstInstallmentPayment(contractId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/contracts/${contractId}/confirm-first-installment`, {}, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        return response;
      }),
      catchError(err => {
        console.error('❌ Erro ao confirmar pagamento da 1ª parcela:', err);
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Cancelar contrato (ContractDraft).
   * Body: preferência `motivo`, depois `observacoes` / `observations` (gravados em PlaceholdersDataJson._erpContractCancel).
   */
  cancelContract(
    contractId: number,
    body?: { motivo?: string; observacoes?: string; observations?: string }
  ): Observable<any> {
    const payload: Record<string, string> = {};
    const m = body?.motivo?.trim();
    const o = body?.observacoes?.trim();
    const en = body?.observations?.trim();
    if (m) payload['motivo'] = m;
    else if (o) payload['observacoes'] = o;
    else if (en) payload['observations'] = en;
    return this.http.put<any>(`${this.baseUrl}/contracts/${contractId}/cancelar`, payload, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        
        if (response.success) {
          return {
            success: true,
            message: response.message || 'Contrato cancelado com sucesso!',
            contract: response.contract ? this.mapContractFromDto(response.contract) : null,
            canceladoEm: response.canceladoEm,
            motivoCancelamento: response.motivoCancelamento,
            dadosContrato: response.dadosContrato
          };
        }
        
        return response;
      }),
      catchError(err => { 
        console.error('❌ Erro ao cancelar contrato:', err);
        this.errorService.handleHttpError(err); 
        return throwError(() => err); 
      })
    );
  }

  /**
   * Congela o contrato (ContractDraft) por período em dias (ex.: 30/60/90) a partir de startDate.
   * PUT {{baseUrl}}/contracts/{id}/congelar
   */
  freezeContract(contractId: number, payload: ContractFreezePayload): Observable<ContractFreezeResponse> {
    const url = `${this.baseUrl}/contracts/${contractId}/congelar`;
    return this.http.put<ContractFreezeResponse>(url, payload, { headers: this.jsonAuthHeaders() }).pipe(
      map((response) => ({
        success: response?.success ?? true,
        message: response?.message,
        status: response?.status ?? (response as any)?.Status,
        freezeStartUtc: response?.freezeStartUtc ?? (response as any)?.FreezeStartUtc,
        freezeEndUtc: response?.freezeEndUtc ?? (response as any)?.FreezeEndUtc
      })),
      catchError((err) => {
        this.errorService.handleHttpError(err, 'Erro ao congelar contrato');
        return throwError(() => err);
      })
    );
  }

  /**
   * Descongela o contrato antes do fim do período.
   * PUT {{baseUrl}}/contracts/{id}/descongelar
   */
  unfreezeContract(contractId: number): Observable<ContractUnfreezeResponse> {
    const url = `${this.baseUrl}/contracts/${contractId}/descongelar`;
    return this.http.put<ContractUnfreezeResponse>(url, {}, { headers: this.jsonAuthHeaders() }).pipe(
      map((response) => ({
        success: response?.success ?? true,
        message: response?.message,
        status: response?.status ?? (response as any)?.Status
      })),
      catchError((err) => {
        this.errorService.handleHttpError(err, 'Erro ao descongelar contrato');
        return throwError(() => err);
      })
    );
  }

  // Substituir veículo do contrato (PUT com payload completo)
  replaceVehicle(contractId: number, replacementData: {
    productId?: number | null;
    vehicleSnapshot: {
      plate: string;
      model: string;
      year: number;
      color: string;
      brand: string;
      renavam: string;
      chassis: string;
      currentKm: number;
      inspectionNotes?: string;
    };
    driver: {
      name: string;
      cpfCnpj: string;
      cnhNumber: string;
      cnhExpiryDate: string;
      email: string;
      phone: string;
      street: string;
      number: string;
      complement?: string;
      district: string;
      city: string;
      state: string;
      zipCode: string;
    };
  }): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/contracts/${contractId}/substituir-veiculo`, replacementData, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        
        if (response.success) {
          return {
            success: true,
            message: response.message || 'Veículo substituído com sucesso!',
            contract: response.contract ? this.mapContractFromDto(response.contract) : null
          };
        }
        
        return response;
      }),
      catchError(err => { 
        console.error('❌ Erro ao substituir veículo:', err);
        this.errorService.handleHttpError(err); 
        return throwError(() => err); 
      })
    );
  }

  // ===== MÉTODOS PARA CONTRATOS B2B =====

  /**
   * Cria um novo contrato B2B
   * @param contractData Dados do contrato
   * @returns Observable com o contrato criado
   */
  createB2bContract(contractData: any): Observable<any> {
    // Novo endpoint padronizado pelo backend
    const url = `${this.baseUrl}/b2b/contracts/criar`;

    // Se for FormData (upload de arquivo), não enviar Content-Type JSON
    const isFormData = typeof FormData !== 'undefined' && contractData instanceof FormData;

    const options = isFormData
      ? { headers: this.bearerHeaders() }
      : { headers: this.jsonAuthHeaders() };

    return this.http.post<any>(this.addFunctionCode(url), contractData, options).pipe(
      map(response => {
        if (response.success) {
          this.errorService.showInfoMessage(response.message || 'Contrato B2B criado com sucesso!');
        }
        return response;
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualiza um contrato B2B existente
   * PUT /b2b/contracts/editar/{id}
   */
  updateB2bContract(contractId: number, contractData: any): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/editar/${contractId}`;
    return this.http.put<any>(this.addFunctionCode(url), contractData, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        this.errorService.showInfoMessage(response?.message || 'Contrato B2B atualizado com sucesso!');
        return response;
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  cancelB2bContract(contractId: number, motivo?: string): Observable<any> {
    const payload = motivo ? { motivo } : {};
    const url = `${this.baseUrl}/b2b/contracts/cancelar/${contractId}`;
    return this.http.put<any>(this.addFunctionCode(url), payload, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        if (response?.success) {
          return {
            success: true,
            message: response.message || 'Contrato B2B cancelado com sucesso!',
            contract: response.contract || response.data || null
          };
        }
        return response;
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Lista contratos B2B com paginação
   * @param page Número da página (padrão: 1)
   * @param pageSize Tamanho da página (padrão: 10)
   * @returns Observable com resposta paginada de contratos B2B
   */
  /**
   * Totais e quebra por contrato: frota, agendamentos, manutenções, orçamentos e multas.
   * GET /b2b/contracts/portfolio-health?periodDays=
   */
  getB2bPortfolioHealth(periodDays: number = 30): Observable<any> {
    const params = new HttpParams().set('periodDays', String(periodDays));
    const url = `${this.baseUrl}/b2b/contracts/portfolio-health?${params.toString()}`;
    return this.http.get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() });
  }

  listB2bContracts(page: number = 1, pageSize: number = 10, customerId?: number | null): Observable<B2bContractPagedResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    if (customerId != null && customerId > 0) {
      params = params.set('customerId', customerId.toString());
    }

    const url = `${this.baseUrl}/b2b/contracts/listar?${params.toString()}`;
    
    return this.http
      .get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() })
      .pipe(
      map(response => {
        // API atual: { totalCount, page, pageSize, totalPages, items[] } (PascalCase nos itens)
        // Legado: { data, pagination } ou { Data, Pagination }
        const rawList =
          response.items ??
          response.Items ??
          response.data ??
          response.Data ??
          [];
        const list = (Array.isArray(rawList) ? rawList : []).map((dto: any) =>
          this.mapB2bContractFromDto(dto)
        );

        const totalItems = Number(
          response.totalCount ??
            response.TotalCount ??
            response.pagination?.totalItems ??
            response.Pagination?.TotalItems ??
            list.length
        );

        const resolvedPageSize = Number(
          response.pageSize ??
            response.PageSize ??
            response.pagination?.pageSize ??
            response.Pagination?.PageSize ??
            pageSize
        );

        const totalPages = Math.max(
          1,
          Number(
            response.totalPages ??
              response.TotalPages ??
              response.pagination?.totalPages ??
              response.Pagination?.TotalPages ??
              (totalItems > 0
                ? Math.ceil(totalItems / (resolvedPageSize || 1))
                : 1)
          )
        );

        const currentPage = Number(
          response.page ??
            response.Page ??
            response.pagination?.currentPage ??
            response.Pagination?.CurrentPage ??
            page
        );

        return {
          data: list,
          pagination: {
            currentPage,
            pageSize: resolvedPageSize,
            totalItems,
            totalPages,
            hasPreviousPage:
              response.pagination?.hasPreviousPage ??
              response.Pagination?.HasPreviousPage ??
              currentPage > 1,
            hasNextPage:
              response.pagination?.hasNextPage ??
              response.Pagination?.HasNextPage ??
              currentPage < totalPages
          }
        };
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Mapeia DTO do backend para B2bContract
   */
  private mapB2bContractFromDto(dto: any): B2bContract {
    return {
      id: dto.Id || dto.id,
      customerId: dto.CustomerId || dto.customerId,
      customerName: dto.CustomerName || dto.customerName || null,
      documentTemplateVersionId: dto.DocumentTemplateVersionId || dto.documentTemplateVersionId,
      documentTemplateVersionNumber: dto.DocumentTemplateVersionNumber || dto.documentTemplateVersionNumber || null,
      contractNumber: dto.ContractNumber || dto.contractNumber,
      contractName: dto.ContractName || dto.contractName,
      startDate: dto.StartDate || dto.startDate,
      endDate: dto.EndDate || dto.endDate,
      status: dto.Status || dto.status,
      cnpj: dto.Cnpj || dto.cnpj,
      companyName: dto.CompanyName || dto.companyName,
      companyAddress: dto.CompanyAddress || dto.companyAddress || null,
      fichaCadastralUrl: dto.FichaCadastralUrl || dto.fichaCadastralUrl || null,
      contractDocumentUrl: dto.ContractDocumentUrl || dto.contractDocumentUrl || null,
      contractDocumentId: dto.ContractDocumentId || dto.contractDocumentId || null,
      clausesAndConditions: dto.ClausesAndConditions || dto.clausesAndConditions || null,
      unit: dto.Unit || dto.unit || null,
      salesperson: dto.Salesperson || dto.salesperson || null,
      observations: dto.Observations || dto.observations || null,
      documents: dto.Documents || dto.documents || dto.ContractDocuments || dto.contractDocuments || [],
      contractItems: dto.ContractItems || dto.contractItems || [],
      vehicles: dto.Vehicles || dto.vehicles || [],
      drivers: dto.Drivers || dto.drivers || [],
      createdAt: dto.CreatedAt || dto.createdAt,
      updatedAt: dto.UpdatedAt || dto.updatedAt || null,
      createdBy: dto.CreatedBy || dto.createdBy || null,
      updatedBy: dto.UpdatedBy || dto.updatedBy || null
    };
  }

  /**
   * Busca detalhes completos de um contrato B2B
   * @param contractId ID do contrato
   * @returns Observable com detalhes completos do contrato B2B
   */
  getB2bContractById(contractId: number): Observable<B2bContractDetail> {
    const url = `${this.baseUrl}/b2b/contracts/visualizar/${contractId}`;
    const finalUrl = this.addFunctionCode(url);
    
    // eslint-disable-next-line no-console
    
    return this.http.get<any>(finalUrl, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        // eslint-disable-next-line no-console
        
        // Verificar se a resposta tem a estrutura esperada
        if (!response) {
          // eslint-disable-next-line no-console
          console.error('[ContractsService] Resposta vazia ou null');
          throw new Error('Resposta do servidor está vazia');
        }

        // Se a resposta tem uma propriedade 'data', usar ela
        const dataToMap = response.data || response;
        
        // eslint-disable-next-line no-console
        
        const mapped = this.mapB2bContractDetailFromDto(dataToMap);
        // eslint-disable-next-line no-console
        return mapped;
      }),
      catchError(err => {
        // eslint-disable-next-line no-console
        console.error('[ContractsService] Erro completo ao buscar contrato B2B:', {
          error: err,
          status: err?.status,
          message: err?.message,
          url: finalUrl
        });
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Mapeia DTO completo do backend para B2bContractDetail
   */
  private mapB2bContractDetailFromDto(dto: any): B2bContractDetail {
    // eslint-disable-next-line no-console
    
    // Garantir que temos um objeto válido
    if (!dto) {
      // eslint-disable-next-line no-console
      console.error('[ContractsService] DTO vazio ou inválido');
      throw new Error('Resposta do servidor inválida');
    }

    // Mapear campos principais - verificar ambos PascalCase e camelCase
    // Usar verificação explícita para garantir que os valores sejam mapeados
    const mapped: B2bContractDetail = {
      id: dto.Id !== undefined ? dto.Id : (dto.id !== undefined ? dto.id : 0),
      customerId: dto.CustomerId !== undefined ? dto.CustomerId : (dto.customerId !== undefined ? dto.customerId : 0),
      customerName: dto.CustomerName !== undefined && dto.CustomerName !== null ? dto.CustomerName : (dto.customerName !== undefined && dto.customerName !== null ? dto.customerName : null),
      documentTemplateVersionId: dto.DocumentTemplateVersionId !== undefined ? dto.DocumentTemplateVersionId : (dto.documentTemplateVersionId !== undefined ? dto.documentTemplateVersionId : 0),
      documentTemplateVersionNumber: dto.DocumentTemplateVersionNumber !== undefined && dto.DocumentTemplateVersionNumber !== null ? dto.DocumentTemplateVersionNumber : (dto.documentTemplateVersionNumber !== undefined && dto.documentTemplateVersionNumber !== null ? dto.documentTemplateVersionNumber : null),
      contractNumber: dto.ContractNumber !== undefined && dto.ContractNumber !== null ? dto.ContractNumber : (dto.contractNumber !== undefined && dto.contractNumber !== null ? dto.contractNumber : ''),
      contractName: dto.ContractName !== undefined && dto.ContractName !== null ? dto.ContractName : (dto.contractName !== undefined && dto.contractName !== null ? dto.contractName : ''),
      startDate: dto.StartDate !== undefined && dto.StartDate !== null ? dto.StartDate : (dto.startDate !== undefined && dto.startDate !== null ? dto.startDate : ''),
      endDate: dto.EndDate !== undefined && dto.EndDate !== null ? dto.EndDate : (dto.endDate !== undefined && dto.endDate !== null ? dto.endDate : ''),
      status: dto.Status !== undefined && dto.Status !== null ? dto.Status : (dto.status !== undefined && dto.status !== null ? dto.status : ''),
      cnpj: dto.Cnpj !== undefined && dto.Cnpj !== null ? dto.Cnpj : (dto.cnpj !== undefined && dto.cnpj !== null ? dto.cnpj : ''),
      companyName: dto.CompanyName !== undefined && dto.CompanyName !== null ? dto.CompanyName : (dto.companyName !== undefined && dto.companyName !== null ? dto.companyName : ''),
      companyAddress: dto.CompanyAddress !== undefined && dto.CompanyAddress !== null ? dto.CompanyAddress : (dto.companyAddress !== undefined && dto.companyAddress !== null ? dto.companyAddress : null),
      fichaCadastralUrl: dto.FichaCadastralUrl !== undefined && dto.FichaCadastralUrl !== null ? dto.FichaCadastralUrl : (dto.fichaCadastralUrl !== undefined && dto.fichaCadastralUrl !== null ? dto.fichaCadastralUrl : null),
      contractDocumentUrl: dto.ContractDocumentUrl !== undefined && dto.ContractDocumentUrl !== null ? dto.ContractDocumentUrl : (dto.contractDocumentUrl !== undefined && dto.contractDocumentUrl !== null ? dto.contractDocumentUrl : null),
      contractDocumentId: dto.ContractDocumentId !== undefined && dto.ContractDocumentId !== null ? dto.ContractDocumentId : (dto.contractDocumentId !== undefined && dto.contractDocumentId !== null ? dto.contractDocumentId : null),
      clausesAndConditions: dto.ClausesAndConditions !== undefined && dto.ClausesAndConditions !== null ? dto.ClausesAndConditions : (dto.clausesAndConditions !== undefined && dto.clausesAndConditions !== null ? dto.clausesAndConditions : null),
      unit: dto.Unit !== undefined && dto.Unit !== null ? dto.Unit : (dto.unit !== undefined && dto.unit !== null ? dto.unit : null),
      salesperson: dto.Salesperson !== undefined && dto.Salesperson !== null ? dto.Salesperson : (dto.salesperson !== undefined && dto.salesperson !== null ? dto.salesperson : null),
      observations: dto.Observations !== undefined && dto.Observations !== null ? dto.Observations : (dto.observations !== undefined && dto.observations !== null ? dto.observations : null),
      depositAmount: dto.DepositAmount !== undefined ? Number(dto.DepositAmount) : (dto.depositAmount !== undefined ? Number(dto.depositAmount) : 0),
      documents: Array.isArray(dto.Documents) ? dto.Documents : (Array.isArray(dto.documents) ? dto.documents : (Array.isArray(dto.ContractDocuments) ? dto.ContractDocuments : (Array.isArray(dto.contractDocuments) ? dto.contractDocuments : []))),
      contractItems: [],
      vehicles: Array.isArray(dto.Vehicles) ? dto.Vehicles : (Array.isArray(dto.vehicles) ? dto.vehicles : []),
      drivers: (Array.isArray(dto.Drivers) ? dto.Drivers : (Array.isArray(dto.drivers) ? dto.drivers : []))
        .map((d: any) => this.mapB2bDriverFromDto(d)),
      createdAt: dto.CreatedAt !== undefined && dto.CreatedAt !== null ? dto.CreatedAt : (dto.createdAt !== undefined && dto.createdAt !== null ? dto.createdAt : ''),
      updatedAt: dto.UpdatedAt !== undefined && dto.UpdatedAt !== null ? dto.UpdatedAt : (dto.updatedAt !== undefined && dto.updatedAt !== null ? dto.updatedAt : null),
      createdBy: dto.CreatedBy !== undefined && dto.CreatedBy !== null ? dto.CreatedBy : (dto.createdBy !== undefined && dto.createdBy !== null ? dto.createdBy : null),
      updatedBy: dto.UpdatedBy !== undefined && dto.UpdatedBy !== null ? dto.UpdatedBy : (dto.updatedBy !== undefined && dto.updatedBy !== null ? dto.updatedBy : null)
    };

    // eslint-disable-next-line no-console

    // Mapear itens do contrato
    const items = dto.ContractItems || dto.contractItems || [];
    // eslint-disable-next-line no-console
    
    if (Array.isArray(items) && items.length > 0) {
      mapped.contractItems = items.map((item: any) => {
        try {
          return this.mapB2bContractItemFromDto(item);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[ContractsService] Erro ao mapear item:', error, item);
          return null;
        }
      }).filter((item: any) => item !== null);
    }

    // eslint-disable-next-line no-console

    return mapped;
  }

  /**
   * Mapeia DTO de ContractItem para B2bContractItem
   */
  private mapB2bContractItemFromDto(dto: any): any {
    // Mapear VehicleSnapshots (array) ou VehicleSnapshot (singular)
    let vehicleSnapshots: any[] = [];
    if (dto.VehicleSnapshots && Array.isArray(dto.VehicleSnapshots)) {
      vehicleSnapshots = dto.VehicleSnapshots.map((vs: any) => this.mapB2bVehicleSnapshotFromDto(vs));
    } else if (dto.vehicleSnapshots && Array.isArray(dto.vehicleSnapshots)) {
      vehicleSnapshots = dto.vehicleSnapshots.map((vs: any) => this.mapB2bVehicleSnapshotFromDto(vs));
    } else if (dto.VehicleSnapshot) {
      vehicleSnapshots = [this.mapB2bVehicleSnapshotFromDto(dto.VehicleSnapshot)];
    } else if (dto.vehicleSnapshot) {
      vehicleSnapshots = [this.mapB2bVehicleSnapshotFromDto(dto.vehicleSnapshot)];
    }

    return {
      id: dto.Id || dto.id,
      b2bContractId: dto.B2BContractId || dto.b2bContractId,
      contractNumber: dto.ContractNumber || dto.contractNumber,
      vehicleSnapshots: vehicleSnapshots,
      vehicleSnapshot: vehicleSnapshots.length > 0 ? vehicleSnapshots[0] : null, // Manter compatibilidade
      itemNumber: dto.ItemNumber || dto.itemNumber,
      motorcycleType: dto.MotorcycleType || dto.motorcycleType,
      quantity: dto.Quantity || dto.quantity,
      unitValue: dto.UnitValue || dto.unitValue,
      paymentType: dto.PaymentType || dto.paymentType,
      installmentCount: dto.InstallmentCount || dto.installmentCount,
      state: dto.State || dto.state,
      city: dto.City || dto.city,
      contractType: dto.ContractType || dto.contractType,
      deliveryType: dto.DeliveryType || dto.deliveryType,
      pole: dto.Pole || dto.pole,
      deliveryDate: dto.DeliveryDate || dto.deliveryDate,
      returnDate: dto.ReturnDate || dto.returnDate || null,
      customerPickupDate: dto.CustomerPickupDate || dto.customerPickupDate || null,
      accessoriesDescription: dto.AccessoriesDescription || dto.accessoriesDescription || null,
      trackerType: dto.TrackerType || dto.trackerType || null,
      hasBox: dto.HasBox || dto.hasBox || false,
      status: dto.Status || dto.status,
      isReplacement: dto.IsReplacement || dto.isReplacement || false,
      replacedItemId: dto.ReplacedItemId || dto.replacedItemId || null,
      replacedVehicleId: dto.ReplacedVehicleId || dto.replacedVehicleId || null,
      driverAssociations: dto.DriverAssociations || dto.driverAssociations || [],
      createdAt: dto.CreatedAt || dto.createdAt,
      updatedAt: dto.UpdatedAt || dto.updatedAt || null,
      createdBy: dto.CreatedBy || dto.createdBy || null,
      updatedBy: dto.UpdatedBy || dto.updatedBy || null
    };
  }

  /**
   * Mapeia DTO de VehicleSnapshot para B2bVehicleSnapshot
   */
  private mapB2bVehicleSnapshotFromDto(dto: any): any {
    return {
      id: dto.Id || dto.id,
      b2bContractItemId: dto.B2BContractItemId || dto.b2bContractItemId,
      plate: dto.Plate || dto.plate,
      model: dto.Model || dto.model,
      year: dto.Year || dto.year,
      color: dto.Color || dto.color,
      brand: dto.Brand || dto.brand,
      renavam: dto.Renavam || dto.renavam,
      chassis: dto.Chassis || dto.chassis,
      currentKm: dto.CurrentKm || dto.currentKm,
      inspectionNotes: dto.InspectionNotes || dto.inspectionNotes || null,
      operationalStatus: dto.OperationalStatus || dto.operationalStatus || null,
      trackerType: dto.TrackerType || dto.trackerType || null,
      trackerId: dto.TrackerId || dto.trackerId || null,
      hasBox: dto.HasBox || dto.hasBox || false,
      hasPhoneSupport: dto.HasPhoneSupport || dto.hasPhoneSupport || false,
      hasDriver: dto.HasDriver !== undefined ? dto.HasDriver : (dto.hasDriver !== undefined ? dto.hasDriver : false),
      driverName: dto.DriverName ?? dto.driverName ?? null,
      b2BContractDriverId: dto.B2BContractDriverId ?? dto.b2BContractDriverId ?? null,
      b2BContractItemDriverId: dto.B2BContractItemDriverId ?? dto.b2BContractItemDriverId ?? null,
      createdAt: dto.CreatedAt || dto.createdAt,
      updatedAt: dto.UpdatedAt || dto.updatedAt || null,
      createdBy: dto.CreatedBy || dto.createdBy || null,
      updatedBy: dto.UpdatedBy || dto.updatedBy || null
    };
  }

  /**
   * Adiciona um novo item (veículo) ao contrato B2B
   * @param contractId ID do contrato
   * @param itemData Dados do item a ser adicionado
   * @returns Observable com o item criado
   */
  addB2bContractItem(contractId: number, itemData: any): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/items`;
    
    return this.http.post<any>(this.addFunctionCode(url), itemData, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        // A resposta pode vir em diferentes formatos
        return response.data || response || itemData;
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Cria itens de contrato B2B em lote (com múltiplos veículos)
   * @param contractId ID do contrato
   * @param itemData Dados do item com array de vehicleSnapshots
   * @returns Observable com resposta da criação
   */
  addB2bContractItemBatch(contractId: number, itemData: any): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/items/criar-em-lote`;
    
    return this.http.post<any>(this.addFunctionCode(url), itemData, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        return response.data || response || itemData;
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualiza um pedido (item) do contrato B2B.
   * PUT /b2b/contracts/{contractId}/pedidos/editar/{pedidoId}
   */
  editB2bPedido(contractId: number, pedidoId: number, payload: any): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/pedidos/editar/${pedidoId}`;

    return this.http.put<any>(this.addFunctionCode(url), payload, { headers: this.jsonAuthHeaders() }).pipe(
      map((response) => response?.data ?? response),
      catchError((err) => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Upload do documento do contrato B2B
   * @param contractId ID do contrato
   * @param file Arquivo do documento (PDF, DOC, etc.)
   * @returns Observable com a resposta do upload
   */
  uploadB2bContractDocument(
    contractId: number,
    file: File,
    options?: {
      signatureReferenceImage?: File | null;
      signaturePositionHint?: string | null;
      signaturePosition?: {
        page: number;
        x: number;
        y: number;
        xPct: number;
        yPct: number;
      } | null;
      signatureSize?: {
        width: number;
        height: number;
      } | null;
    }
  ): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/documento/upload`;
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (options?.signatureReferenceImage) {
      formData.append('signatureReferenceImage', options.signatureReferenceImage, options.signatureReferenceImage.name);
    }
    if (options?.signaturePositionHint) {
      formData.append('signaturePositionHint', options.signaturePositionHint);
    }
    if (options?.signaturePosition) {
      formData.append('signaturePosition', JSON.stringify(options.signaturePosition));
    }
    if (options?.signatureSize) {
      formData.append('signatureSize', JSON.stringify(options.signatureSize));
    }
    return this.http
      .post<any>(this.addFunctionCode(url), formData, { headers: this.bearerHeaders() })
      .pipe(
      map(response => response.data || response),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  signB2bDocument(documentId: number, signatureBase64: string): Observable<any> {
    const url = `${this.baseUrl}/b2b/documents/${documentId}/assinar`;
    const payload = {
      signature: signatureBase64,
      signatureBase64
    };
    const finalUrl = this.addFunctionCode(url);
    return this.http.post<any>(finalUrl, payload, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => response?.data || response),
      catchError(() => {
        // Compatibilidade: alguns backends expõem este endpoint como PUT.
        return this.http.put<any>(finalUrl, payload, { headers: this.jsonAuthHeaders() }).pipe(
          map(response => response?.data || response),
          catchError(err => {
            this.errorService.handleHttpError(err);
            return throwError(() => err);
          })
        );
      })
    );
  }

  /**
   * Vincula um condutor a um veículo de um item do contrato B2B
   * @param contractId ID do contrato
   * @param itemId ID do item
   * @param assignmentData Dados da vinculação (b2BContractItemId, b2BContractItemVehicleSnapshotId, b2BContractDriverId, startDate, endDate, status, notes)
   * @returns Observable com resposta da vinculação
   */
  assignDriverToVehicle(contractId: number, itemId: number, assignmentData: any): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/vehicles/${itemId}/condutores/vincular`;
    
    return this.http.post<any>(this.addFunctionCode(url), assignmentData, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        return response.data || response || assignmentData;
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Desvincula apenas o condutor — o veículo permanece no pedido.
   * POST /b2b/contracts/{contractId}/vehicles/{itemId}/condutores/desvincular/{driverKey}
   * `driverKey` aceita B2BContractDriverId, Id da associação ou Id do snapshot do veículo.
   * Não use a rota de devolução aqui: ela pode remover o veículo do contrato.
   */
  unassignDriverFromVehicle(
    contractId: number,
    itemId: number,
    driverKey: number,
    body: { motivo: string; returnDate?: string }
  ): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/vehicles/${itemId}/condutores/desvincular/${driverKey}`;
    const payload: { motivo: string; returnDate?: string } = { motivo: String(body?.motivo ?? '').trim() };
    if (body?.returnDate) {
      payload.returnDate = body.returnDate;
    }
    return this.http.post<any>(this.addFunctionCode(url), payload, { headers: this.jsonAuthHeaders() }).pipe(
      map((response) => response),
      catchError((err) => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca detalhes de uma vinculação de condutor a veículo pelo ID da vinculação
   * @param contractId ID do contrato
   * @param assignmentId ID da vinculação
   * @returns Observable com detalhes da vinculação
   */
  getDriverVehicleAssignment(contractId: number, assignmentId: number, silent = false): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/vehicles/vinculacao/${assignmentId}`;
    
    return this.http.get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        return response.data || response.Data || response;
      }),
      catchError(err => {
        if (!silent) {
          this.errorService.handleHttpError(err);
        }
        return silent ? of(null) : throwError(() => err);
      })
    );
  }

  /**
   * Busca detalhes de uma vinculação de condutor a veículo pelo ID do veículo
   * @param contractId ID do contrato
   * @param vehicleId ID do veículo (B2BContractItemVehicleSnapshotId)
   * @returns Observable com detalhes da vinculação
   */
  getDriverVehicleAssignmentByVehicleId(contractId: number, vehicleId: number, silent = false): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/vehicles/${vehicleId}/vinculacao`;
    
    return this.http.get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        if (response && (response.VehiclePlate || response.vehiclePlate || response.B2BContractItemId)) {
          return response;
        }
        return response.data || response.Data || response;
      }),
      catchError(err => {
        if (!silent) {
          this.errorService.handleHttpError(err);
        }
        return silent ? of(null) : throwError(() => err);
      })
    );
  }

  /**
   * Busca todas as vinculações de um contrato (para casos onde não temos o ID da vinculação)
   * @param contractId ID do contrato
   * @returns Observable com lista de vinculações
   */
  getDriverVehicleAssignments(contractId: number): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/vehicles/vinculacoes`;
    
    return this.http.get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        return response.data || response.Data || response || [];
      }),
      catchError(err => {
        // Se o endpoint não existir, retornar array vazio
        return of([]);
      })
    );
  }

  /**
   * Lista veículos de um contrato B2B com paginação
   * @param contractId ID do contrato
   * @param page Número da página (padrão: 1)
   * @param pageSize Tamanho da página (padrão: 10)
   * @returns Observable com resposta paginada de veículos
   */
  listB2bContractVehicles(
    contractId: number,
    page: number = 1,
    pageSize: number = 10,
    search?: string | null,
    assigned?: boolean | null,
    silent = false
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    const term = (search || '').trim();
    if (term) {
      params = params.set('search', term);
    }
    if (assigned === true) {
      params = params.set('assigned', 'true');
    } else if (assigned === false) {
      params = params.set('assigned', 'false');
    }

    const url = `${this.baseUrl}/b2b/contracts/${contractId}/vehicles/listar?${params.toString()}`;
    
    return this.http.get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        const pagination = response.Pagination || response.pagination || {};
        const currentPage = pagination.CurrentPage || pagination.currentPage || page;
        const totalItems = pagination.TotalItems || pagination.totalItems || response.TotalItems || 0;
        const totalPages = pagination.TotalPages || pagination.totalPages || Math.max(1, Math.ceil((totalItems || 1) / pageSize));
        return {
          data: response.Data || response.data || response.Items || response.items || [],
          pagination: {
            currentPage,
            pageSize: pagination.PageSize || pagination.pageSize || pageSize,
            totalItems,
            totalPages,
            hasPreviousPage: pagination.HasPreviousPage ?? pagination.hasPreviousPage ?? currentPage > 1,
            hasNextPage: pagination.HasNextPage ?? pagination.hasNextPage ?? currentPage < totalPages
          },
          assignedCount: response.AssignedCount ?? response.assignedCount ?? null,
          availableCount: response.AvailableCount ?? response.availableCount ?? null
        };
      }),
      catchError(err => {
        if (!silent) {
          this.errorService.handleHttpError(err);
        }
        return silent
          ? of({ data: [], pagination: { totalPages: 1, totalItems: 0 } })
          : throwError(() => err);
      })
    );
  }

  /**
   * Percorre as páginas de veículos do contrato (tamanho 10) sem disparar pageSize grande.
   */
  listAllB2bContractVehicles(contractId: number, pageSize: number = 10, maxPages: number = 50): Observable<any[]> {
    const acc: any[] = [];
    const loadPage = (page: number): Observable<any[]> =>
      this.listB2bContractVehicles(contractId, page, pageSize, null, null, page > 1).pipe(
        switchMap((response: any) => {
          const chunk = Array.isArray(response?.data) ? response.data : [];
          acc.push(...chunk);
          const totalPages = Number(response?.pagination?.totalPages || 1);
          if (chunk.length > 0 && page < totalPages && page < maxPages) {
            return loadPage(page + 1);
          }
          return of(acc);
        })
      );
    return loadPage(1);
  }

  /**
   * Frota do contrato (veículos ± condutor) — preferencial para agenda B2B.
   * GET /b2b/contracts/{id}/frota — fallback para vehicles/listar.
   */
  listB2bContractFrota(contractId: number, page: number = 1, pageSize: number = 100, search?: string | null): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    const term = (search || '').trim();
    if (term) {
      params = params.set('search', term);
    }

    const url = `${this.baseUrl}/b2b/contracts/${contractId}/frota?${params.toString()}`;

    return this.http.get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() }).pipe(
      map(response => ({
        data: response.Data || response.data || response.Items || response.items || [],
        pagination: response.Pagination || response.pagination || null
      })),
      catchError(() => this.listB2bContractVehicles(contractId, page, pageSize, search))
    );
  }

  /**
   * Busca itens de um contrato B2B com paginação
   * @param contractId ID do contrato
   * @param page Número da página (padrão: 1)
   * @param pageSize Tamanho da página (padrão: 10)
   * @param search Número do pedido, placa, tipo de moto ou status
   * @returns Observable com resposta paginada de itens
   */
  getB2bContractItems(contractId: number, page: number = 1, pageSize: number = 10, search?: string | null): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    const term = (search || '').trim();
    if (term) {
      params = params.set('search', term);
    }

    const url = `${this.baseUrl}/b2b/contracts/${contractId}/items/listar?${params.toString()}`;
    const finalUrl = this.addFunctionCode(url);

    return this.http.get<any>(finalUrl, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        let itemsData: any[] = [];

        if (Array.isArray(response)) {
          itemsData = response;
        } else if (response?.data && Array.isArray(response.data)) {
          itemsData = response.data;
        } else if (response?.Data && Array.isArray(response.Data)) {
          itemsData = response.Data;
        } else if (response?.items && Array.isArray(response.items)) {
          itemsData = response.items;
        } else if (response?.Items && Array.isArray(response.Items)) {
          itemsData = response.Items;
        }

        const mappedItems = itemsData.map((item: any) => this.mapB2bContractItemFromDto(item));
        const pagination = response?.pagination || response?.Pagination || {};

        const totalItems =
          pagination.totalItems ||
          pagination.TotalItems ||
          response?.totalItems ||
          response?.TotalItems ||
          mappedItems.length;

        const resolvedPageSize =
          pagination.pageSize || pagination.PageSize || pageSize;

        return {
          data: mappedItems,
          pagination: {
            currentPage: pagination.currentPage || pagination.CurrentPage || page,
            pageSize: resolvedPageSize,
            totalItems,
            totalPages:
              pagination.totalPages ||
              pagination.TotalPages ||
              Math.max(1, Math.ceil(totalItems / Math.max(1, resolvedPageSize))),
            hasPreviousPage: pagination.hasPreviousPage ?? pagination.HasPreviousPage ?? page > 1,
            hasNextPage:
              pagination.hasNextPage ??
              pagination.HasNextPage ??
              page * resolvedPageSize < totalItems
          }
        };
      }),
      catchError(err => {
        console.error('[ContractsService] Erro ao buscar itens do contrato:', err);
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca detalhes de um item específico do contrato B2B
   * @param contractId ID do contrato
   * @param itemId ID do item
   * @returns Observable com detalhes do item
   */
  getB2bContractItemById(contractId: number, itemId: number): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/items/visualizar/${itemId}`;
    
    return this.http
      .get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() })
      .pipe(
      map(response => {
        const dto = response?.data ?? response?.Data ?? response;
        return this.mapB2bContractItemFromDto(dto);
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualiza um item do contrato B2B
   * @param contractId ID do contrato
   * @param itemId ID do item
   * @param itemData Dados atualizados do item
   * @returns Observable com o item atualizado
   */
  updateB2bContractItem(contractId: number, itemId: number, itemData: any): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/items/${itemId}`;
    
    return this.http.put<any>(this.addFunctionCode(url), itemData, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        return response.data || response || itemData;
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Desativa um item do contrato B2B (soft delete)
   * @param contractId ID do contrato
   * @param itemId ID do item
   * @returns Observable com resultado da operação
   */
  deactivateB2bContractItem(contractId: number, itemId: number): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/items/${itemId}`;

    return this.http
      .delete<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() })
      .pipe(
      map(response => {
        return response;
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Cancela um item específico do contrato B2B sem afetar o contrato inteiro.
   * Endpoint esperado: PUT .../b2b/contracts/{contractId}/items/cancelar/{itemId}
   */
  cancelB2bContractItem(contractId: number, itemId: number, motivo?: string): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/items/cancelar/${itemId}`;
    const payload = motivo ? { motivo } : {};

    return this.http.put<any>(this.addFunctionCode(url), payload, { headers: this.jsonAuthHeaders() }).pipe(
      map((response) => response),
      catchError((err) => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Substitui o snapshot de veículo de um item B2B (troca de moto no pedido).
   * Backend esperado: PUT .../b2b/contracts/{contractId}/items/{itemId}/vehicles/{vehicleSnapshotId}/substituir
   */
  substituirB2bItemVeiculo(
    contractId: number,
    itemId: number,
    vehicleSnapshotId: number,
    body: {
      vehicleSnapshot: {
        vehicleId?: number | null;
        plate: string;
        model: string;
        year: number;
        color?: string;
        brand?: string;
        renavam?: string;
        chassis?: string;
        currentKm?: number;
        inspectionNotes?: string | null;
        trackerType?: string | null;
        trackerId?: string | null;
        hasBox?: boolean;
      };
      observacao?: string | null;
    }
  ): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/items/${itemId}/vehicles/${vehicleSnapshotId}/substituir`;
    return this.http.put<any>(this.addFunctionCode(url), body, { headers: this.jsonAuthHeaders() }).pipe(
      map((response) => response),
      catchError((err) => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Registra devolução do item B2B (nível pedido).
   * POST .../b2b/contracts/{contractId}/items/{itemId}/devolucao
   * Body: `{ "motivo": "..." }` (opcionalmente `returnDate` se a API exigir).
   */
  registrarDevolucaoB2bItem(
    contractId: number,
    itemId: number,
    body: { motivo: string; returnDate?: string }
  ): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/items/${itemId}/devolucao`;
    const payload: { motivo: string; returnDate?: string } = { motivo: String(body?.motivo ?? '').trim() };
    if (body?.returnDate) {
      payload.returnDate = body.returnDate;
    }
    return this.http.post<any>(this.addFunctionCode(url), payload, { headers: this.jsonAuthHeaders() }).pipe(
      map((response) => response),
      catchError((err) => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Registra devolução de um veículo (snapshot) específico de um item B2B.
   * PUT /b2b/contracts/{contractId}/items/{itemId}/vehicles/{id}/devolucao
   */
  registrarDevolucaoB2bItemVeiculo(
    contractId: number,
    itemId: number,
    vehicleId: number,
    body: { motivo: string; returnDate?: string }
  ): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/items/${itemId}/vehicles/${vehicleId}/devolucao`;
    const payload: { motivo: string; returnDate?: string } = { motivo: String(body?.motivo ?? '').trim() };
    if (body?.returnDate) {
      payload.returnDate = body.returnDate;
    }
    return this.http.put<any>(this.addFunctionCode(url), payload, { headers: this.jsonAuthHeaders() }).pipe(
      map((response) => response),
      catchError((err) => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Histórico de substituições / devoluções do veículo no item (opcional — se o backend expuser).
   */
  listarHistoricoB2bItemVeiculo(
    contractId: number,
    itemId: number,
    vehicleSnapshotId: number
  ): Observable<any[]> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/items/${itemId}/vehicles/${vehicleSnapshotId}/historico`;
    return this.http
      .get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() })
      .pipe(
      map((response) => {
        const data = response?.data ?? response?.Data ?? response;
        return Array.isArray(data) ? data : [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Busca detalhes de um condutor específico de um contrato B2B
   * @param contractId ID do contrato
   * @param driverId ID do condutor
   * @returns Observable com detalhes do condutor
   */
  getB2bContractDriverById(contractId: number, driverId: number): Observable<B2bContractDriver> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/drivers/visualizar/${driverId}`;

    return this.http
      .get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() })
      .pipe(
      map(response => {
        const driver = response.data || response.Data || response;
        return this.mapB2bDriverFromDto(driver);
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /** Mapeia DTO de condutor B2B (Pascal/camel) para o model tipado. */
  private mapB2bDriverFromDto(driver: any): B2bContractDriver {
    return {
      id: driver.Id || driver.id,
      name: driver.Name || driver.name || '',
      driverCpf: driver.DriverCpf || driver.driverCpf || '',
      driverCnh: driver.DriverCnh || driver.driverCnh || '',
      cnhCategory: driver.CnhCategory || driver.cnhCategory || null,
      cnhExpirationDate: driver.CnhExpirationDate || driver.cnhExpirationDate || null,
      employeeRegistration: driver.EmployeeRegistration || driver.employeeRegistration || null,
      admissionDate: driver.AdmissionDate || driver.admissionDate || null,
      birthDate: driver.BirthDate || driver.birthDate || null,
      coordinatorName: driver.CoordinatorName || driver.coordinatorName || null,
      leaderName: driver.LeaderName || driver.leaderName || null,
      phone: driver.Phone || driver.phone || '',
      email: driver.Email || driver.email || '',
      address: driver.Address || driver.address || '',
      addressStreet: driver.AddressStreet || driver.addressStreet || null,
      addressNumber: driver.AddressNumber || driver.addressNumber || null,
      addressNeighborhood: driver.AddressNeighborhood || driver.addressNeighborhood || null,
      addressZipCode: driver.AddressZipCode || driver.addressZipCode || null,
      addressCity: driver.AddressCity || driver.addressCity || null,
      addressState: driver.AddressState || driver.addressState || null,
      contractNumber: driver.ContractNumber || driver.contractNumber || 'N/A',
      b2bContractId: driver.B2BContractId || driver.b2bContractId,
      termoResponsabilidadeUrl: driver.TermoResponsabilidadeUrl || driver.termoResponsabilidadeUrl,
      cnhDocumentUrl: driver.CnhDocumentUrl || driver.cnhDocumentUrl,
      createdAt: driver.CreatedAt || driver.createdAt,
      updatedAt: driver.UpdatedAt || driver.updatedAt
    };
  }

  /**
   * Cria um novo condutor em um contrato B2B
   * @param contractId ID do contrato
   * @param driverData Dados do condutor para criação
   * @returns Observable com resposta da criação
   */
  createB2bContractDriver(contractId: number, driverData: any): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/drivers/criar`;

    return this.http.post<any>(this.addFunctionCode(url), driverData, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        if (response.success) {
          this.errorService.showSuccessMessage(response.message || 'Condutor criado com sucesso!');
        }
        return response;
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualiza um condutor de um contrato B2B
   * @param contractId ID do contrato
   * @param driverId ID do condutor
   * @param driverData Dados do condutor para atualização
   * @returns Observable com resposta da atualização
   */
  updateB2bContractDriver(contractId: number, driverId: number, driverData: any): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/drivers/editar/${driverId}`;

    return this.http.put<any>(this.addFunctionCode(url), driverData, { headers: this.jsonAuthHeaders() }).pipe(
      map(response => {
        if (response.success) {
          this.errorService.showSuccessMessage(response.message || 'Condutor atualizado com sucesso!');
        }
        return response;
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  uploadB2bDriverDocument(
    contractId: number,
    driverId: number,
    file: File,
    documentType: 'CNH' | 'TermoResponsabilidade'
  ): Observable<B2bContractDriver> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/drivers/${driverId}/documentos/upload?documentType=${encodeURIComponent(documentType)}`;
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http
      .post<any>(this.addFunctionCode(url), formData, { headers: this.bearerHeaders() })
      .pipe(
        map(response => {
          const driver = response?.data || response?.Data || response;
          this.errorService.showSuccessMessage(response?.message || 'Documento enviado com sucesso.');
          return this.mapB2bDriverFromDto(driver);
        }),
        catchError(err => {
          this.errorService.handleHttpError(err);
          return throwError(() => err);
        })
      );
  }

  /**
   * Exclui um condutor de um contrato B2B
   * @param contractId ID do contrato
   * @param driverId ID do condutor
   * @returns Observable com resposta da exclusão
   */
  deleteB2bContractDriver(contractId: number, driverId: number): Observable<any> {
    const url = `${this.baseUrl}/b2b/contracts/${contractId}/drivers/excluir/${driverId}`;

    return this.http
      .delete<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() })
      .pipe(
      map(response => {
        if (response.success) {
          this.errorService.showInfoMessage(response.message || 'Condutor excluído com sucesso!');
        }
        return response;
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca condutores de um contrato B2B com paginação
   * @param contractId ID do contrato
   * @param page Número da página (padrão: 1)
   * @param pageSize Tamanho da página (padrão: 10)
   * @param search Nome, CPF, CNH ou matrícula
   * @returns Observable com resposta paginada de condutores
   */
  getB2bContractDrivers(contractId: number, page: number = 1, pageSize: number = 10, search?: string | null): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    const term = (search || '').trim();
    if (term) {
      params = params.set('search', term);
    }

    const url = `${this.baseUrl}/b2b/contracts/${contractId}/drivers/listar?${params.toString()}`;

    return this.http
      .get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() })
      .pipe(
      map(response => {
        // A resposta pode vir em diferentes formatos
        // Se vier com estrutura paginada
        if (response.data && Array.isArray(response.data)) {
          return {
            data: response.data.map((d: any) => this.mapB2bDriverFromDto(d)),
            pagination: response.pagination || response.Pagination || {
              currentPage: page,
              pageSize: pageSize,
              totalItems: response.totalItems || response.TotalItems || response.data.length,
              totalPages: response.totalPages || response.TotalPages || 1
            }
          };
        } else if (response.Data && Array.isArray(response.Data)) {
          return {
            data: response.Data.map((d: any) => this.mapB2bDriverFromDto(d)),
            pagination: response.Pagination || {
              currentPage: page,
              pageSize: pageSize,
              totalItems: response.TotalItems || response.Data.length,
              totalPages: response.TotalPages || 1
            }
          };
        } else if (Array.isArray(response)) {
          // Fallback: se vier como array direto, retorna sem paginação
          return {
            data: response.map((d: any) => this.mapB2bDriverFromDto(d)),
            pagination: {
              currentPage: page,
              pageSize: pageSize,
              totalItems: response.length,
              totalPages: 1
            }
          };
        }
        return {
          data: [],
          pagination: {
            currentPage: page,
            pageSize: pageSize,
            totalItems: 0,
            totalPages: 0
          }
        };
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  // ===== OPERACIONAL B2B (sinistro, cobrança, reserva, acessório, competência) =====

  getB2bClaims(contractId: number): Observable<B2bClaim[]> {
    return this.http
      .get<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/claims/listar`), { headers: this.jsonAuthHeaders() })
      .pipe(map(r => this.extractOperationalList<B2bClaim>(r)), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  createB2bClaim(contractId: number, data: B2bClaim): Observable<B2bClaim> {
    return this.http
      .post<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/claims/criar`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  updateB2bClaim(contractId: number, id: number, data: B2bClaim): Observable<B2bClaim> {
    return this.http
      .put<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/claims/editar/${id}`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  deleteB2bClaim(contractId: number, id: number): Observable<any> {
    return this.http
      .delete<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/claims/excluir/${id}`), { headers: this.jsonAuthHeaders() })
      .pipe(catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  getB2bClaimById(contractId: number, id: number): Observable<B2bClaim> {
    return this.http
      .get<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/claims/${id}`), { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  listB2bClaimComments(contractId: number, claimId: number): Observable<Array<{
    id: number;
    mensagem: string;
    autor: string;
    autorOrigem: string;
    interno: boolean;
    linhaTipo?: string | null;
    linhaId?: number | null;
    criadoEm: string;
  }>> {
    const url = this.addFunctionCode(
      `${this.baseUrl}/b2b/contracts/${contractId}/claims/${claimId}/comentarios`
    );
    return this.http.get<any>(url, { headers: this.jsonAuthHeaders() }).pipe(
      map(r => {
        const list = Array.isArray(r) ? r : (r?.data ?? r?.items ?? []);
        return (list as any[]).map(c => ({
          id: c.id ?? c.Id,
          mensagem: c.mensagem ?? c.Mensagem ?? c.message ?? '',
          autor: c.autor ?? c.Autor ?? c.authorName ?? '',
          autorOrigem: c.autorOrigem ?? c.AutorOrigem ?? c.authorScope ?? 'System',
          interno: !!(c.interno ?? c.Interno),
          linhaTipo: c.linhaTipo ?? c.LinhaTipo ?? null,
          linhaId: c.linhaId ?? c.LinhaId ?? null,
          criadoEm: c.criadoEm ?? c.CriadoEm ?? c.createdAt ?? ''
        }));
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  addB2bClaimComment(
    contractId: number,
    claimId: number,
    body: { mensagem: string; interno?: boolean }
  ): Observable<{
    id: number;
    mensagem: string;
    autor: string;
    autorOrigem: string;
    interno: boolean;
    linhaTipo?: string | null;
    linhaId?: number | null;
    criadoEm: string;
  }> {
    const url = this.addFunctionCode(
      `${this.baseUrl}/b2b/contracts/${contractId}/claims/${claimId}/comentarios`
    );
    return this.http.post<any>(url, body, { headers: this.jsonAuthHeaders() }).pipe(
      map(c => {
        const raw = c?.data ?? c;
        return {
          id: raw.id ?? raw.Id,
          mensagem: raw.mensagem ?? raw.Mensagem ?? raw.message ?? '',
          autor: raw.autor ?? raw.Autor ?? raw.authorName ?? '',
          autorOrigem: raw.autorOrigem ?? raw.AutorOrigem ?? raw.authorScope ?? 'System',
          interno: !!(raw.interno ?? raw.Interno),
          linhaTipo: raw.linhaTipo ?? raw.LinhaTipo ?? null,
          linhaId: raw.linhaId ?? raw.LinhaId ?? null,
          criadoEm: raw.criadoEm ?? raw.CriadoEm ?? raw.createdAt ?? ''
        };
      }),
      catchError(err => {
        this.errorService.handleHttpError(err);
        return throwError(() => err);
      })
    );
  }

  uploadB2bClaimAttachment(contractId: number, claimId: number, file: File, budgetId?: number | null): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    let url = `${this.baseUrl}/b2b/contracts/${contractId}/claims/${claimId}/attachments/upload`;
    if (budgetId != null) url += `?budgetId=${budgetId}`;
    return this.http
      .post<any>(this.addFunctionCode(url), formData, { headers: this.bearerHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  deleteB2bClaimAttachment(contractId: number, claimId: number, attachmentId: number): Observable<any> {
    return this.http
      .delete<any>(
        this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/claims/${claimId}/attachments/${attachmentId}`),
        { headers: this.jsonAuthHeaders() }
      )
      .pipe(catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  getB2bCharges(contractId: number, type?: string): Observable<B2bOperationalCharge[]> {
    let url = `${this.baseUrl}/b2b/contracts/${contractId}/charges/listar`;
    if (type) url += `?type=${encodeURIComponent(type)}`;
    return this.http
      .get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() })
      .pipe(map(r => this.extractOperationalList<B2bOperationalCharge>(r)), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  createB2bCharge(contractId: number, data: B2bOperationalCharge): Observable<B2bOperationalCharge> {
    return this.http
      .post<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/charges/criar`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  updateB2bCharge(contractId: number, id: number, data: B2bOperationalCharge): Observable<B2bOperationalCharge> {
    return this.http
      .put<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/charges/editar/${id}`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  deleteB2bCharge(contractId: number, id: number): Observable<any> {
    return this.http
      .delete<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/charges/excluir/${id}`), { headers: this.jsonAuthHeaders() })
      .pipe(catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  getB2bReservations(contractId: number): Observable<B2bVehicleReservation[]> {
    return this.http
      .get<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/reservations/listar`), { headers: this.jsonAuthHeaders() })
      .pipe(map(r => this.extractOperationalList<B2bVehicleReservation>(r)), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  createB2bReservation(contractId: number, data: B2bVehicleReservation): Observable<B2bVehicleReservation> {
    return this.http
      .post<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/reservations/criar`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  updateB2bReservation(contractId: number, id: number, data: B2bVehicleReservation): Observable<B2bVehicleReservation> {
    return this.http
      .put<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/reservations/editar/${id}`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  deleteB2bReservation(contractId: number, id: number): Observable<any> {
    return this.http
      .delete<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/reservations/excluir/${id}`), { headers: this.jsonAuthHeaders() })
      .pipe(catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  getB2bAccessories(contractId: number): Observable<B2bAccessory[]> {
    return this.http
      .get<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/accessories/listar`), { headers: this.jsonAuthHeaders() })
      .pipe(map(r => this.extractOperationalList<B2bAccessory>(r)), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  createB2bAccessory(contractId: number, data: B2bAccessory): Observable<B2bAccessory> {
    return this.http
      .post<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/accessories/criar`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  updateB2bAccessory(contractId: number, id: number, data: B2bAccessory): Observable<B2bAccessory> {
    return this.http
      .put<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/accessories/editar/${id}`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  deleteB2bAccessory(contractId: number, id: number): Observable<any> {
    return this.http
      .delete<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/accessories/excluir/${id}`), { headers: this.jsonAuthHeaders() })
      .pipe(catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  getB2bItemAccessories(contractId: number, itemId: number): Observable<B2bContractItemAccessory[]> {
    return this.http
      .get<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/items/${itemId}/accessories/listar`), { headers: this.jsonAuthHeaders() })
      .pipe(map(r => this.extractOperationalList<B2bContractItemAccessory>(r)), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  linkB2bItemAccessory(contractId: number, itemId: number, data: B2bContractItemAccessory): Observable<B2bContractItemAccessory> {
    return this.http
      .post<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/items/${itemId}/accessories/vincular`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  unlinkB2bItemAccessory(contractId: number, itemId: number, id: number): Observable<any> {
    return this.http
      .delete<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/items/${itemId}/accessories/excluir/${id}`), { headers: this.jsonAuthHeaders() })
      .pipe(catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  getB2bBillingPeriods(contractId: number, year?: number, month?: number): Observable<B2bBillingPeriod[]> {
    let url = `${this.baseUrl}/b2b/contracts/${contractId}/billing-periods/listar`;
    const qs: string[] = [];
    if (year != null) qs.push(`year=${year}`);
    if (month != null) qs.push(`month=${month}`);
    if (qs.length) url += `?${qs.join('&')}`;
    return this.http
      .get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() })
      .pipe(map(r => this.extractOperationalList<B2bBillingPeriod>(r)), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  createB2bBillingPeriod(contractId: number, data: B2bBillingPeriod): Observable<B2bBillingPeriod> {
    return this.http
      .post<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/billing-periods/criar`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  updateB2bBillingPeriod(contractId: number, id: number, data: B2bBillingPeriod): Observable<B2bBillingPeriod> {
    return this.http
      .put<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/billing-periods/editar/${id}`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  deleteB2bBillingPeriod(contractId: number, id: number): Observable<any> {
    return this.http
      .delete<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/billing-periods/excluir/${id}`), { headers: this.jsonAuthHeaders() })
      .pipe(catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  getB2bTrafficFines(contractId: number): Observable<B2bTrafficFine[]> {
    return this.http
      .get<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/traffic-fines/listar`), { headers: this.jsonAuthHeaders() })
      .pipe(map(r => this.extractOperationalList<B2bTrafficFine>(r)), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  createB2bTrafficFine(contractId: number, data: B2bTrafficFine): Observable<B2bTrafficFine> {
    return this.http
      .post<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/traffic-fines/criar`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  updateB2bTrafficFine(contractId: number, id: number, data: B2bTrafficFine): Observable<B2bTrafficFine> {
    return this.http
      .put<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/traffic-fines/editar/${id}`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  deleteB2bTrafficFine(contractId: number, id: number): Observable<any> {
    return this.http
      .delete<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/traffic-fines/excluir/${id}`), { headers: this.jsonAuthHeaders() })
      .pipe(catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  getB2bSubstitutions(contractId: number): Observable<B2bVehicleSubstitution[]> {
    return this.http
      .get<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/substitutions/listar`), { headers: this.jsonAuthHeaders() })
      .pipe(map(r => this.extractOperationalList<B2bVehicleSubstitution>(r)), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  createB2bSubstitution(contractId: number, data: B2bVehicleSubstitution): Observable<B2bVehicleSubstitution> {
    return this.http
      .post<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/substitutions/criar`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  updateB2bSubstitution(contractId: number, id: number, data: B2bVehicleSubstitution): Observable<B2bVehicleSubstitution> {
    return this.http
      .put<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/substitutions/editar/${id}`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  deleteB2bSubstitution(contractId: number, id: number): Observable<any> {
    return this.http
      .delete<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/substitutions/excluir/${id}`), { headers: this.jsonAuthHeaders() })
      .pipe(catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  getB2bReturnEvents(contractId: number): Observable<B2bVehicleReturnEvent[]> {
    return this.http
      .get<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/return-events/listar`), { headers: this.jsonAuthHeaders() })
      .pipe(map(r => this.extractOperationalList<B2bVehicleReturnEvent>(r)), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  createB2bReturnEvent(contractId: number, data: B2bVehicleReturnEvent): Observable<B2bVehicleReturnEvent> {
    return this.http
      .post<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/return-events/criar`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  updateB2bReturnEvent(contractId: number, id: number, data: B2bVehicleReturnEvent): Observable<B2bVehicleReturnEvent> {
    return this.http
      .put<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/return-events/editar/${id}`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  deleteB2bReturnEvent(contractId: number, id: number): Observable<any> {
    return this.http
      .delete<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/return-events/excluir/${id}`), { headers: this.jsonAuthHeaders() })
      .pipe(catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  getB2bFleetDeliveries(contractId: number): Observable<B2bFleetDelivery[]> {
    return this.http
      .get<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/fleet-deliveries/listar`), { headers: this.jsonAuthHeaders() })
      .pipe(map(r => this.extractOperationalList<B2bFleetDelivery>(r)), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  createB2bFleetDelivery(contractId: number, data: B2bFleetDelivery): Observable<B2bFleetDelivery> {
    return this.http
      .post<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/fleet-deliveries/criar`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  updateB2bFleetDelivery(contractId: number, id: number, data: B2bFleetDelivery): Observable<B2bFleetDelivery> {
    return this.http
      .put<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/fleet-deliveries/editar/${id}`), data, { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  deleteB2bFleetDelivery(contractId: number, id: number): Observable<any> {
    return this.http
      .delete<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/fleet-deliveries/excluir/${id}`), { headers: this.jsonAuthHeaders() })
      .pipe(catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  getB2bServiceOrders(contractId: number, status?: string, isScheduled?: boolean): Observable<B2bContractServiceOrder[]> {
    let url = `${this.baseUrl}/b2b/contracts/${contractId}/service-orders/listar`;
    const qs: string[] = [];
    if (status) qs.push(`status=${encodeURIComponent(status)}`);
    if (isScheduled !== undefined) qs.push(`isScheduled=${isScheduled}`);
    if (qs.length) url += `?${qs.join('&')}`;
    return this.http
      .get<any>(this.addFunctionCode(url), { headers: this.jsonAuthHeaders() })
      .pipe(map(r => this.extractOperationalList<B2bContractServiceOrder>(r)), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  getB2bServiceOrderById(contractId: number, id: number): Observable<B2bContractServiceOrder> {
    return this.http
      .get<any>(this.addFunctionCode(`${this.baseUrl}/b2b/contracts/${contractId}/service-orders/${id}`), { headers: this.jsonAuthHeaders() })
      .pipe(map(r => r?.data ?? r), catchError(err => { this.errorService.handleHttpError(err); return throwError(() => err); }));
  }

  private extractOperationalList<T>(response: any): T[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.Data)) return response.Data;
    return [];
  }
}

/**
 * Body esperado pelo POST de devolução B2B (`.../items/{itemId}/devolucao`):
 * ```json
 * { "motivo": "Fim do período contratado.", "returnDate": "2026-04-15T12:00:00Z" }
 * ```
 */
export function formatB2bDevolucaoReturnDateUtc(value: string | null | undefined): string {
  const d = value ? new Date(value) : new Date();
  const t = isNaN(d.getTime()) ? new Date() : d;
  return t.toISOString().replace(/\.\d{3}Z$/, 'Z');
}
